import { t, UUID, Money, DateTimeISO, Status } from '../common.ts';
import { PROMOTION_CLAIM_STATUSES } from './promo-claim-status.ts';

export {
  PROMOTION_CLAIM_STATUSES,
  PROMOTION_CLAIM_OUTCOME,
  promotionClaimOutcome,
  type PromotionClaimStatus,
} from './promo-claim-status.ts';

export const PromotionKind = t.Union([
  t.Literal('welcome'),
  t.Literal('first_deposit'),
  t.Literal('reload'),
  t.Literal('cashback'),
  t.Literal('turnover_rebate'),
  t.Literal('daily_checkin'),
  // ponytail: 'deposit' and 'display_only' are contract-reserved, not productised —
  // no BO form and no calcReward branch (claiming either → PROMO-002). Wire a branch
  // only if product asks for one; removing a member is a cross-service contract change.
  t.Literal('deposit'),
  // A coupon campaign. It carries a fixed amount and is handed out by code, so it is
  // deliberately NOT self-claimable: claimPromotion answers PROMO-002 for it and only
  // redeemCoupon reaches its calcReward branch.
  t.Literal('by_admin'),
  t.Literal('display_only'),
]);

export const PromotionApplyOn = t.Union([
  t.Literal('deposit'),
  t.Literal('all'),
  t.Literal('manual'),
]);

export const PromotionAudience = t.Union([
  t.Literal('all'),
  t.Literal('group'),
]);

// Promotions support an extra 'archived' state (soft-delete preserving audit
// history). Scoped here — the shared Status (common.ts) stays active|inactive
// because coupons, lucky-spin, and the other 4 domains never archive.
export const PromotionStatus = t.Union([
  t.Literal('active'),
  t.Literal('inactive'),
  t.Literal('archived'),
]);

export const PromotionView = t.Object(
  {
    id: UUID,
    name: t.String(),
    code: t.String(),
    kind: PromotionKind,
    applyOn: PromotionApplyOn,
    audience: PromotionAudience,
    periodFrom: DateTimeISO,
    periodTo: t.Union([DateTimeISO, t.Null()]),
    conditions: t.Record(t.String(), t.Unknown()),
    rewardRules: t.Record(t.String(), t.Unknown()),
    turnoverMultiplier: t.String(),
    maxClaimsTotal: t.Union([t.Integer(), t.String()]),
    maxClaimsPerMember: t.Union([t.Integer(), t.String()]),
    autoApprove: t.Boolean(),
    expiryDays: t.Union([t.Integer(), t.Null()]),
    groupIds: t.Optional(t.Array(UUID)),
    status: PromotionStatus,
    createdAt: DateTimeISO,
    /**
     * Whether the CALLING member may start a NEW claim on this promotion, for the
     * kinds whose eligibility is a fact about the member rather than about the
     * campaign (today only `first_deposit`, which is spent once the member's
     * first deposit is approved — #149).
     *
     * It is scoped to STARTING one, and that scope is load-bearing: the verdict
     * flips to `false` at the moment the first deposit is approved, which is also
     * the moment a reservation taken beforehand is about to become a live bonus.
     * A promotion the caller already holds a live claim on is therefore shown
     * regardless of this field, or the client deletes the card from under the
     * member whose bonus is running.
     *
     * Present only on a member-authenticated list, and only on the rows it is
     * about. ABSENT MEANS NOT EVALUATED — an operator list, an anonymous browse,
     * a kind with no such gate — and a client must keep showing the promotion in
     * every one of those cases. Only `false` is a verdict.
     */
    memberEligible: t.Optional(t.Boolean()),
  },
  { $id: 'PromotionView' },
);

export const CreatePromotionRequest = t.Object(
  {
    // The brand this promotion belongs to. Optional on the wire because a
    // brand-resolved caller already carries one in `x-agent-id`; the backoffice
    // does not (its host is the console, not a brand) and must send it.
    //
    // An explicit `null` asks for a PLATFORM campaign — one with no brand,
    // claimable by every brand's members. It is a different request from
    // omitting the field, which still means "infer mine", and only a
    // platform-root caller may make it (PROMO-406). Its money figures then need
    // a currency of their own, because there is no brand to inherit one from
    // (PROMO-084).
    agentId: t.Optional(t.Union([UUID, t.Null()])),
    name: t.String({ minLength: 1, maxLength: 120 }),
    code: t.String({ minLength: 2, maxLength: 64 }),
    kind: PromotionKind,
    applyOn: PromotionApplyOn,
    audience: PromotionAudience,
    description: t.Optional(t.String({ maxLength: 1000 })),
    bannerUrl: t.Optional(t.String({ maxLength: 512 })),
    groupIds: t.Optional(t.Array(UUID)),
    conditions: t.Record(t.String(), t.Unknown(), { default: {} }),
    rewardRules: t.Record(t.String(), t.Unknown(), { default: {} }),
    turnoverMultiplier: t.Optional(t.String({ pattern: '^[0-9]+(\\.[0-9]{1,2})?$' })),
    periodFrom: DateTimeISO,
    periodTo: t.Optional(DateTimeISO),
    maxClaimsTotal: t.Optional(t.Integer({ minimum: 0 })),
    maxClaimsPerMember: t.Optional(t.Integer({ minimum: 0 })),
    autoApprove: t.Optional(t.Boolean()),
    // `null` is a value, not a mistake: the backoffice form sends it when the
    // turnover multiplier is 0 (nothing to finish, so nothing to expire), and on
    // an edit it is what clears the expiry the row already carries. The repo has
    // always taken it; while this refused it, no promotion with a zero multiplier
    // — the default for both rebate kinds — could be created from the console.
    expiryDays: t.Optional(t.Union([t.Integer({ minimum: 1, maximum: 365 }), t.Null()])),
    status: t.Optional(PromotionStatus),
  },
  { $id: 'CreatePromotionRequest' },
);

// Mirrors the raw promo.promotion_claims row the routes return (the previous
// bonusAmount/requiredTurnover/currentTurnover shape never existed on the live
// API and had no consumers).
export const PromotionClaimView = t.Object(
  {
    id: UUID,
    promotionId: UUID,
    memberId: UUID,
    agentId: t.Union([UUID, t.Null()]),
    depositId: t.Union([UUID, t.Null()]),
    requestedAmount: Money,
    grantedAmount: Money,
    turnoverRequired: Money,
    turnoverAchieved: Money,
    status: t.Union(PROMOTION_CLAIM_STATUSES.map((s) => t.Literal(s))),
    rejectReason: t.Union([t.String(), t.Null()]),
    periodKey: t.Union([t.String(), t.Null()]),
    txId: t.Union([t.String(), t.Null()]),
    expiresAt: t.Union([DateTimeISO, t.Null()]),
    createdAt: DateTimeISO,
  },
  { $id: 'PromotionClaimView' },
);

// Mirrors the raw promo.coupons row (the previous name/codeMode/fixedCode/
// value/quantity shape never existed on the live API and had no consumers).
export const CouponView = t.Object(
  {
    id: UUID,
    promotionId: UUID,
    code: t.String(),
    maxUses: t.Integer(),
    usedCount: t.Integer(),
    validFrom: t.Union([DateTimeISO, t.Null()]),
    validTo: t.Union([DateTimeISO, t.Null()]),
    status: Status,
    createdAt: DateTimeISO,
  },
  { $id: 'CouponView' },
);

export const LuckySpinConfigView = t.Object(
  {
    id: UUID,
    depositPerSpin: t.Integer(),
    turnoverMultiplier: t.Number({ minimum: 0, maximum: 100 }),
    /** How many spins a member may SPEND in a Bangkok day. 0 = unlimited. */
    maxPerDay: t.Integer(),
    /**
     * How many spin entitlements a member may be GRANTED from deposits in a
     * Bangkok day. 0 = unlimited, and so is an absent value: this column
     * postdates every config that already exists, and the create body is built
     * from this view, so a required field here would refuse every caller
     * written before it.
     */
    maxGrantsPerDay: t.Optional(t.Integer({ minimum: 0 })),
    status: Status,
    description: t.Union([t.String(), t.Null()]),
  },
  { $id: 'LuckySpinConfigView' },
);
